import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ListagemParcelasAPI from "./ListagemParcelasAPI";
import EditDataParcelaDialog from "./EditDataParcelaDialog";
import useParcelasAPI from "./useParcelasAPI";

export default function SelecaoCondicaoParcelamentoAPI({
    valorTotal,
    defaultValues,
    menorValorParcelamento,
    onChange,
    possuiParcelas = true,
    isCartao = false,
    isCheque = false,
    permiteEdicaoVencimento = false
}: SelecaoCondicaoParcelamentoAPIProps) {

    const [modalEditDataParcela, setModalEditDataParcela] = useState<null | TModalEditDataAPI>(null)
    
    const { 
        listaParcelas, 
        valorTotalComJuros, 
        percentualJuros,
        handleDataChange,
        loading,
        error 
    } = useParcelasAPI(defaultValues, valorTotal, isCartao, isCheque)

    const condicaoParcelas = useMemo(() => defaultValues.maximo_parcelas, [defaultValues])

    const qtdParcelasOpcao = useMemo(() => {
        const { maximo_parcelas } = defaultValues

        if (maximo_parcelas >= menorValorParcelamento) return menorValorParcelamento
        return maximo_parcelas
    }, [defaultValues, menorValorParcelamento])

    const prctDiferencaTotal = useMemo(() => {
        return `${percentualJuros?.toFixed(2) || '0.00'}%`;
    }, [percentualJuros])

    // Funções Manipuladoras
    const handleChangeInput = useCallback((prop: TStatePropsAPI, value: any) => {
        let params = {
            ...defaultValues,
            [prop]: value,
            parcelas: listaParcelas
        }

        onChange({
            ...params
        })
    }, [onChange, defaultValues, listaParcelas])

    const handleOpenEditModalData = useCallback((value: null | TModalEditDataAPI) => {
        setModalEditDataParcela(value)
    }, [])

    useEffect(() => {
        if (JSON.stringify(listaParcelas) !== JSON.stringify(defaultValues.parcelas)) {
            handleChangeInput("parcelas", listaParcelas);
        }
    }, [listaParcelas, defaultValues.parcelas, handleChangeInput])

    const mostrarParcelas = useMemo(() => {
        if (!possuiParcelas) return false;
        if (isCartao && defaultValues.intervalo <= 1) return false;
        return true
    }, [defaultValues, isCartao, possuiParcelas])

    return (
        <>
            {
                condicaoParcelas >= 1 &&
                <Box>
                    <FormControl fullWidth>
                        <InputLabel id="qtd-parcelas">Parcelas</InputLabel>
                        <Select
                            label="Parcelas"
                            labelId="qtd-parcelas"
                            size="medium"
                            value={defaultValues.qtdParcelas}
                            onChange={(e) => handleChangeInput("qtdParcelas", e.target.value)}
                            disabled={loading}
                        >
                           {!mostrarParcelas && <MenuItem value={0}>Nenhuma</MenuItem>}
                            {mostrarParcelas &&
                                Array.from({ length: qtdParcelasOpcao }, (_, index) => index + 1).map((item, index) => (
                                    <MenuItem key={index} value={item}>{item}x</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Box>
            }

            {error && (
                <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}>
                    Erro ao calcular parcelas: {error}
                </Box>
            )}

            {
                (listaParcelas?.length > 0 && defaultValues.qtdParcelas > 0) &&
                <ListagemParcelasAPI
                    handleOpenModalEdit={(data: TModalEditDataAPI) => handleOpenEditModalData(data)}
                    loading={loading}
                    blockEdit={isCartao}
                    {...{ listaParcelas, valorTotalComJuros, prctDiferencaTotal, permiteEdicaoVencimento }}
                />
            }

            {
                modalEditDataParcela &&
                <EditDataParcelaDialog
                    open={modalEditDataParcela ? true : false}
                    blockEdit={isCartao}
                    defaultValue={modalEditDataParcela}
                    maxParcelas={defaultValues.maximo_parcelas}
                    handleClose={() => handleOpenEditModalData(null)}
                    {...{ handleDataChange, listaParcelas }}
                />
            }
        </>
    )
}

interface SelecaoCondicaoParcelamentoAPIProps {
    valorTotal: number,
    menorValorParcelamento: number,
    defaultValues: TCondicaoParcelasAPI,
    onChange: (arg: TCondicaoParcelasAPI) => void
    possuiParcelas?: boolean
    isCartao?: boolean
    isCheque?: boolean
    permiteEdicaoVencimento?: boolean
}

type TStatePropsAPI = "qtdParcelas" | "dataEntrada" | "parcelas"

export type TModalEditDataAPI = {
    index: number,
    date: string
}

export type TCondicaoParcelasAPI = {
    maximo_parcelas: number,
    taxa: number
    parcelas: TListaParcelasAPI[];
    qtdParcelas: number
    intervalo: number
    condicao_pagamento_id?: number
    forma_recebimento_id?: number
    tipo_publico: string
    codigo_publico: string
    diaVencimentoPadrao: number
}

export type TListaParcelasAPI = {
    numero: number;
    valor: number;
    vencimento: string;
    dias_ate_vencimento?: number;
}

export type TSimularParcelamentoRequest = {
    condicao_pagamento_id: number;
    valor: number;
    parcelas: number;
    datas_vencimento?: string[];
    tipo_publico: string;
    codigo_publico: number;
    forma_recebimento_id: number;
}

export type TSimularParcelamentoResponse = {
    parcelas: TListaParcelasAPI[];
    valor: number;
    percentual_juros: number;
    taxa_mensal: number;
    taxa_diaria: number;
    metodo_calculo: string;
    valor_futuro_calculado: number;
    dias_ate_calculo: number;
}
